import React, { useContext, useEffect, useRef, useState } from 'react';
import { ReportNavContext } from '../components/reportNavContext';
import { useTranslation } from 'react-i18next';
import { p, tp, useP } from '../i18n/useP';
import {
  getPlView, patchPlAccountLine, importPlExcel, setMonthly, setLineForecast,
  createLine, updateLine, deleteLine, reorderLines, renameSection,
  fillCsForecast, fillTaxForecast,
} from '../api/financials_pl';
import PageShell from '../components/PageShell';
import Card from '../components/Card';
import { usePersistentSet, usePersistentValue } from '../hooks/usePersistentState';
import ViewSwitch from '../components/ViewSwitch';
import UnitSwitch from '../components/UnitSwitch';
import SegmentedToggle from '../components/SegmentedToggle';
import '../components/EditableCell.css';   // standard page-table-layout / page-table / p-btn
import './ProfitLossPage.css';

const MONTHS = ['Jan', tp('phrases:p_7b8852ec47'), 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', tp('phrases:p_39184c9361'), 'Sep', 'Oct', 'Nov', tp('phrases:p_2bc2249a06')];

// Affichage des montants. Par défaut en kCHF (milliers) — compact pour voir
// toute l'année sans scroll. thousands=false → au franc près (unité).
function chf(v, thousands = true) {
  const n = Number(v || 0);
  if (n === 0) return '–';
  return Math.round(thousands ? n / 1000 : n).toLocaleString('de-CH');
}

// Chevron SVG (identique au Bilan) : pointe à droite, pivote 90° à l'ouverture.
const PlChevron = ({ open }) => (
  <svg className={`pl-chev${open ? ' is-open' : ''}`} viewBox="0 0 24 24" width="12" height="12" aria-hidden>
    <polyline points="9 6 15 12 9 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* `readOnly` — RESTITUTION PURE : aucune écriture possible. Distinct de
   `actualsOnly`, qui ne fait que borner l'affichage aux mois clôturés (les deux
   étaient confondus : un rapport « au réel » exposait en fait tout l'outillage
   de saisie). La saisie du réel vit dans le hub Données ; la structure dans
   Paramètres ; la prévision dans le hub Prévisionnel. */
/* Filtres de la barre de commande — un bloc du compte de résultat par pilule.
   `null` = tout. La valeur ajoutée n'est montrée qu'avec les deux blocs qui la
   composent (règle des sous-totaux vérifiables). */
const CB_FILTERS = {
  direct: { sections: ['revenue', 'cogs'], subs: ['cogs_goods', 'cogs_other', 'value_added'] },
  personnel: { sections: ['personnel'], subs: [] },
  indirect: { sections: ['other_opex'], subs: [] },
};

export default function ProfitLossPage({ activeTenant, addToast, year, onYearChange, embedded, forecastMode = false, sectionFilter = null, subtotalFilter = null, noCanPct = false, noFold = false, actualsOnly = false, readOnly = false, withPrevYear = false, version = null, compareVersion = null, compareLabel = null, unit, commandBar = false, cutoff = null, reloadKey = 0, loadedMonths = false }) {
  const { t } = useTranslation();
  const p = useP();
  const [view, setView]                 = useState(null);
  const [prevView, setPrevView]         = useState(null);   // année N-1 (comparatif réel seul)
  const [loading, setLoading]           = useState(false);
  const [importing, setImporting]       = useState(false);
  const [showStructure, setShowStruct]  = useState(false);
  const [brushMode, setBrushMode]       = useState(false);
  /* Les règles par ligne se voient d'emblée depuis le 2026-09-12 : il n'y a
     plus rien à générer, une règle posée se lit aussitôt. Mais un ÉTAT QUI SE
     LIT n'en montre aucune (2026-09-18) : le compte de résultat de Finance
     PRÉSENTE, il ne planifie pas, et sa colonne de méthodes y proposait de
     changer une règle qu'on ne venait pas régler. Elles restent dans le
     Prévisionnel, qui est fait pour ça. */
  const [fcRules]                       = useState(true);
  const [showModeHelp, setShowModeHelp] = useState(false);  // popup légende des modes
  const [activeLine, setActiveLine]     = useState(null);   // line id | '__unmap__' | null
  const stateKey = `pl_${activeTenant?.id}_${year}`;
  const [expanded, setExpanded]         = usePersistentSet(`${stateKey}_exp`);
  const [expandedSecs, setExpandedSecs] = usePersistentSet(`${stateKey}_secs`);
  const [level, setLevel]               = usePersistentValue(`pl_level_${activeTenant?.id}`, 1);
  const [thousandsLocal, setThousands]  = usePersistentValue(`pl_unit_${activeTenant?.id}`, true);
  // Unité contrôlée par le parent (hub États financiers) si `unit` est fourni ;
  // sinon bascule locale (usage autonome : compte de résultat, prévisionnel).
  const unitControlled = unit !== undefined;
  const thousands = unitControlled ? unit !== 'chf' : thousandsLocal;
  const [editMode, setEditMode]         = useState(false);

  /* ── Barre de commande (écran unique du compte de résultat, maquette validée
     le 2026-08-10) : vue (Réel | Prévisionnel) + mode (Présenter | Éditer réel
     | Planifier) + filtres par bloc + agrégation. Les pilules ne créent AUCUNE
     capacité : chaque état réassigne les props historiques de la page. */
  const [cbVue, setCbVue] = usePersistentValue(`pnl_vue_${activeTenant?.id}`, 'reel');
  const [cbMode, setCbMode] = usePersistentValue(`fc_pnl_mode_${activeTenant?.id}`, 'present');
  const [cbFilter, setCbFilter] = useState('all');
  const [cbAccordion, setCbAccordion] = usePersistentValue(`pnl_accordion_${activeTenant?.id}`, false);
  if (commandBar) {
    readOnly = cbMode === 'present';
    forecastMode = cbMode === 'plan';
    actualsOnly = cbVue === 'reel';
    withPrevYear = cbVue === 'prev';
    const f = CB_FILTERS[cbFilter] || null;
    sectionFilter = f ? f.sections : null;
    subtotalFilter = f ? f.subs : null;
  }
  /* UN ÉCRAN FILTRÉ N'A RIEN À REPLIER (2026-09-25) : le bouton de niveau sert
     à synthétiser la CASCADE entière ; dès qu'une étape ne montre qu'une ou
     deux sections, il proposerait de replier une table de six lignes, et le
     bouton d'aide parlerait de modes qu'on ne choisit pas là. */
  const uniqueSection = Array.isArray(sectionFilter) && sectionFilter.length > 0;
  /* Les règles de planification ne s'affichent QUE là où l'on planifie : un
     état en lecture seule les cachait déjà pour l'écriture, il les cache
     maintenant tout court. Les Données comptables (`actualsOnly`) ne font que charger le
     réalisé : ni règles, ni sources de planification (2026-10-22). */
  const fcMode = fcRules && !readOnly && !actualsOnly;
  // Un mode entraîne sa vue : planifier regarde l'avenir, éditer regarde le
  // réel — la vue reste ensuite modifiable à la main sans quitter le mode.
  useEffect(() => {
    if (!commandBar) return;
    if (cbMode === 'plan') setCbVue('prev');
    if (cbMode === 'edit') setCbVue('reel');
    setEditMode(cbMode === 'edit');
    if (cbMode !== 'edit') { setBrushMode(false); setShowStruct(false); setActiveLine(null); }
    // eslint-disable-next-line
  }, [commandBar, cbMode]);
  const fileRef = useRef();

  async function handleEditValue(accountNumber, month, value) {
    const res = await setMonthly(activeTenant.id, { year, account_number: accountNumber, month, amount: value });
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur mise à jour'), 'error');
  }

  // Saisie prévisionnelle (forecast) sur une reporting line / mois futur.
  async function handleEditForecast(lineId, month, value) {
    const res = await setLineForecast(activeTenant.id, { year, reporting_line_id: lineId, month, amount: value });
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur forecast'), 'error');
  }

  // Cible « Coût des ventes » (pilote la variation de stock prévisionnelle).

  function toggleLine(code) {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(code) ? n.delete(code) : n.add(code);
      return n;
    });
  }
  function toggleSection(code) {
    setExpandedSecs(prev => {
      // « Une à la fois » : ouvrir une section ferme les autres.
      if (commandBar && cbAccordion) return prev.has(code) ? new Set() : new Set([code]);
      const n = new Set(prev);
      n.has(code) ? n.delete(code) : n.add(code);
      return n;
    });
  }
  // Niveaux de lecture (presets) : 1 synthèse · 2 lignes (sans comptes) · 3 comptes.
  function applyLevel(n) {
    setLevel(n);
    const allSecs = (view?.sections || []).map(s => s.code);
    const allLines = (view?.lines || []).map(l => l.id);
    if (n === 1)      { setExpandedSecs(new Set());          setExpanded(new Set()); }
    else if (n === 2) { setExpandedSecs(new Set(allSecs));   setExpanded(new Set()); }
    else              { setExpandedSecs(new Set(allSecs));   setExpanded(new Set(allLines)); }
  }

  // Structure (sections + lignes) vient de la vue.
  const lines    = view?.lines || [];      // [{id,label,section,section_label}]
  const sections = view?.sections || [];   // [{code,label}]

  function reload(silent = false) {
    if (!activeTenant) { setView(null); setPrevView(null); return; }
    if (!silent) setLoading(true);
    // `cutoff` : arrêté d'affichage (Rapports, vue Réel) — le serveur borne la
    // vue à ce mois, cumulés et comparatifs N-1 suivent.
    getPlView(activeTenant.id, year, { ...(version ? { version } : {}), ...(cutoff ? { through: cutoff } : {}), ...(loadedMonths ? { loaded: true } : {}) })
      .then(r => setView(r.data || null))
      .catch(() => addToast(p('Erreur chargement P&L'), 'error'))
      .finally(() => { if (!silent) setLoading(false); });
    // Comparatif année passée : réel seul (colonnes N-1 cumulé) OU prévisionnel
    // (colonne Total N-1, année pleine). Chargé seulement quand utile.
    // `withPrevYear` : vue Prévisionnel des Rapports — mêmes colonnes que le hub
    // Prévisionnel (Total N-1 année pleine), mais en lecture seule.
    // Colonne comparative : exercice N-1 par défaut, ou une VERSION FIGÉE
    // (budget) — même mécanique de colonnes, autre source.
    if (actualsOnly || forecastMode || withPrevYear || compareVersion) {
      (compareVersion
        ? getPlView(activeTenant.id, year, { version: compareVersion })
        : getPlView(activeTenant.id, year - 1))
        .then(r => setPrevView(r.data || null))
        .catch(() => setPrevView(null));
    } else {
      setPrevView(null);
    }
  }

  async function handlePaint(account) {
    if (!activeLine) { addToast(p("Choisis d'abord une reporting line (le pinceau).")); return; }
    const code = activeLine === '__unmap__' ? null : activeLine;
    await handleAssign(account, code);
  }

  // Affecte un compte à une ligne (lineId) ou le démappe (null). Utilisé par
  // le brush ET le drag-and-drop.
  async function handleAssign(account, lineId) {
    const res = await patchPlAccountLine(activeTenant.id, account, lineId || null);
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur affectation'), 'error');
  }

  const lineLabel = id =>
    id === '__unmap__' ? p('Retirer (non mappé)')
      : (lines.find(l => l.id === id)?.label || '—');

  // ── CRUD structure ──────────────────────────────────────────────────────
  async function handleCreateLine(section, label) {
    const res = await createLine(activeTenant.id, section, label);
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur création ligne'), 'error');
  }
  async function handleRenameLine(id, label) {
    const res = await updateLine(activeTenant.id, id, { label });
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur renommage'), 'error');
  }
  async function handleDeleteLine(id) {
    const res = await deleteLine(activeTenant.id, id);
    if (res.data) { if (activeLine === id) setActiveLine(null); reload(true); }
    else addToast(res.error || p('Erreur suppression'), 'error');
  }
  async function handleReorder(ids) {
    const res = await reorderLines(activeTenant.id, ids);
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur réordonnancement'), 'error');
  }
  async function handleRenameSection(code, label) {
    const res = await renameSection(activeTenant.id, code, label);
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur renommage bloc'), 'error');
  }
  async function handleSetMethod(id, patch) {
    const res = await updateLine(activeTenant.id, id, { ...patch, year: view?.year });   // Manuel fige les mois affichés
    if (res.data) reload(true);       // la règle se lit à la relecture, rien à générer (2026-09-12)
    else addToast(res.error || p('Erreur méthode forecast'), 'error');
  }
  // Repasse une ligne CS / Impôts en manuel : efface le taux appliqué (le badge
  // redevient « MAN »). Les montants déjà posés restent éditables à la main.
  async function handleClearRate(id) {
    const res = await updateLine(activeTenant.id, id, { set_forecast_params: true, forecast_rate: null, forecast_method: null });
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur passage manuel'), 'error');
  }
  /* PLUS DE CONNEXION À RÉGLER (décision Olivier, 2026-09-27) : le plan des
     ventes fait le chiffre d'affaires, People les salaires, toujours. Les deux
     bascules « Connecté | Autonome » et leurs hypothèses sont parties. */
  async function handleFillCs(payload) {
    const res = await fillCsForecast(activeTenant.id, payload);
    if (res.data) {
      reload(true);
      addToast(t('phrases:p_6ee8e2a208', { amount: chf(res.data.remaining), months: res.data.forecast_months }));
    } else addToast(res.error || p('Erreur forecast CS'), 'error');
  }
  async function handleFillTax(payload) {
    const res = await fillTaxForecast(activeTenant.id, payload);
    if (res.data) {
      reload(true);
      addToast(t('phrases:p_b6f4c46406', { amount: chf(res.data.december_amount) }));
    } else addToast(res.error || p('Erreur forecast IS'), 'error');
  }

  /* `reloadKey` : la section qui M'ENTOURE a changé une hypothèse dont je
     dépends (une connexion, un taux) et le serveur l'a enregistrée. */
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeTenant, year, actualsOnly, withPrevYear, version, compareVersion, cutoff, reloadKey, loadedMonths]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeTenant) return;

    // L'année n'est pas dans le fichier : on la demande (défaut = année sélectionnée).
    const input = window.prompt(p('Année concernée par ce compte de résultat :'), String(year));
    if (input == null) return;
    const importYear = parseInt(input, 10);
    if (!Number.isFinite(importYear) || importYear < 2000 || importYear > 2100) {
      addToast(t('phrases:p_8fa313abb5'), 'error');
      return;
    }
    if (importYear !== year && onYearChange) onYearChange(importYear);

    setImporting(true);
    try {
      const res = await importPlExcel(activeTenant.id, file, importYear);
      if (res.data) {
        const s = res.data;
        const mois = (s.months || []).join(', ');
        addToast(`Import ${importYear} OK : ${s.accounts_imported} comptes, mois ${mois}.`);
        reload();
      } else {
        addToast(res.error || p('Erreur import'), 'error');
      }
    } catch (err) {
      addToast(p('Erreur import : {{detail}}').replace('{{detail}}', err.response?.data?.detail || err.message), 'error');
    } finally {
      setImporting(false);
    }
  }

  if (!activeTenant) {
    return (
      <PageShell embedded={embedded}>
        <div className="page-table-empty">{p('Sélectionne une entreprise.')}</div>
      </PageShell>
    );
  }

  /* Outils du CR. En mode PRÉVISIONNEL ils vivent dans l'ENTÊTE DE LA CARD
     (2026-09-01) : posés au-dessus par le PageShell, ils flottaient hors de la
     carte qu'ils commandent. */
  const plImport = (readOnly || forecastMode) ? null : (
    <>
      <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFile} />
      <button className="p-btn p-btn--primary" data-tour="pl-import" onClick={() => fileRef.current?.click()} disabled={importing}>
        {importing ? 'Import…' : p('Importer Excel')}
      </button>
    </>
  );
  const plTools = (
        <>
        {!forecastMode && !readOnly && (
          <button
            className={`p-btn ${editMode ? 'p-btn--primary' : 'pl-tool'}`}
            title={t('phrases:p_f219c5d49a')}
            onClick={() => { const v = !editMode; setEditMode(v); if (v) applyLevel(3); else { setBrushMode(false); setShowStruct(false); setActiveLine(null); } }}
          >
            {editMode ? '✓ Terminer' : p('✏️ Éditer')}
          </button>
        )}
        {!forecastMode && editMode && (
          <button
            className={`p-btn ${brushMode ? 'p-btn--primary' : 'p-btn--brush-off'}`}
            onClick={() => { setBrushMode(v => !v); setActiveLine(null); }}
          >
            {brushMode ? '✓ Brush mode' : '🖌 Brush mode'}
          </button>
        )}
        {!forecastMode && editMode && (
          <button
            className={`p-btn ${showStructure ? 'p-btn--secondary' : 'pl-tool'}`}
            onClick={() => setShowStruct(v => !v)}
          >
            {showStructure ? '✓ Structure' : 'Structure'}
          </button>
        )}
        {!unitControlled && (
          <button
            className="p-btn pl-tool"
            title={t('phrases:p_15e4e4a269')}
            onClick={() => setThousands(v => !v)}
          >
            {thousands ? 'kCHF' : 'CHF'}
          </button>
        )}
        {forecastMode && (
          <>
            {/* Les connexions et les règles se voient d'emblée (2026-09-12) ; les
                règles se calculent à la lecture, il n'y a plus rien à générer. */}
            {fcMode && !uniqueSection && (
              <button className="p-btn pl-tool" title={t('phrases:p_c99787ac03')} onClick={() => setShowModeHelp(v => !v)}>
                Mode
              </button>
            )}
          </>
        )}
        {/* Niveau de lecture en un seul bouton cyclique : synthétique → ouvert →
            tout ouvert. UNE SEULE SECTION N'A RIEN À REPLIER (2026-09-25) : sur
            une étape qui ne montre que les revenus, le bouton proposait de
            « synthétiser » une table de six lignes, et le bouton d'aide du mode
            parlait de modes qu'on ne choisit pas là. */}
        {!uniqueSection && (
        <button
          className="p-btn pl-tool"
          title={t('phrases:p_48a792a833')}
          onClick={() => applyLevel(level >= 3 ? 1 : level + 1)}
        >
          {LEVEL_LABELS[level] || LEVEL_LABELS[1]}
        </button>
        )}
        </>
  );

  return (
    <PageShell
      embedded={embedded}
      title={forecastMode ? p('Prévisionnel') : p('Compte de résultat')}
      actions={(forecastMode || readOnly || commandBar || embedded) ? null : plImport}
      tools={(commandBar || forecastMode || embedded) ? null : plTools}
    >
      {commandBar && (
        <div className="pl-cmdbar">
          {/* Rang 1 — le filet : la VUE à gauche (ce qu'on regarde), l'unité à
             droite (réglage d'affichage). */}
          <nav className="tabs">
            <div className="tabs__list">
              <ViewSwitch
                ariaLabel={p('Vue')}
                value={cbVue}
                onChange={setCbVue}
                options={[{ id: 'reel', label: p('Réel') }, { id: 'prev', label: p('Prévisionnel') }]}
              />
            </div>
            <div className="tabs__aside">
              <UnitSwitch value={thousands ? 'kchf' : 'chf'} onChange={(u) => setThousands(u !== 'chf')} />
            </div>
          </nav>
          {/* Rang 2 — le MODE (ce qu'on y fait) et ses outils, filtres à droite. */}
          <div className="pl-cmdbar__row">
            <ViewSwitch
              ariaLabel={p('Mode')}
              value={cbMode}
              onChange={setCbMode}
              options={[
                { id: 'present', label: p('Présenter') },
                { id: 'edit', label: p('Éditer réel') },
                { id: 'plan', label: p('Planifier') },
              ]}
            />
            {/* Les connexions se voient d'emblée (2026-09-12), quel que soit le mode. */}
            {cbMode === 'edit' && (
              <span className="pl-cmdbar__ctx">
                <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFile} />
                <button className="p-btn p-btn--primary" onClick={() => fileRef.current?.click()} disabled={importing}>
                  {importing ? 'Import…' : p('Importer Excel')}
                </button>
                <button
                  className={`p-btn ${brushMode ? 'p-btn--primary' : 'p-btn--brush-off'}`}
                  onClick={() => { setBrushMode(v => !v); setActiveLine(null); }}
                >
                  {brushMode ? '✓ Brush' : '🖌 Brush'}
                </button>
                <button
                  className={`p-btn ${showStructure ? 'p-btn--secondary' : 'pl-tool'}`}
                  onClick={() => setShowStruct(v => !v)}
                >
                  Structure
                </button>
              </span>
            )}
            {cbMode === 'plan' && (
              <span className="pl-cmdbar__ctx">
                {/* « Modèles » : une information, pas un bouton (retour Olivier). */}
                <span className="pl-cmdbar__info">
                  {p('Modèles : LM · MOY · N-1 · % · Cible · Plan personnel · Plan des ventes')}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
      {brushMode && (
        <div className="pl-brushbar">
          <span className="pl-brush-hint">
            {activeLine
              ? <>Pinceau : <b>{lineLabel(activeLine)}</b> — clique un compte pour l'affecter.</>
              : <>Clique une <b>reporting line</b> (ligne grasse) pour charger le pinceau, puis clique les comptes.</>}
          </span>
          <button
            className={`pl-brush-chip${activeLine === '__unmap__' ? ' is-active' : ''}`}
            onClick={() => setActiveLine('__unmap__')}
          >
            Retirer l'affectation
          </button>
          {activeLine && (
            <button className="pl-brush-chip" onClick={() => setActiveLine(null)}>{p('Désélectionner')}</button>
          )}
        </div>
      )}

      {showModeHelp && (
        <div className="pl-fc-modes-overlay" onClick={() => setShowModeHelp(false)}>
          <div className="pl-fc-modes-pop" onClick={e => e.stopPropagation()}>
            <div className="pl-fc-modes-pop__head">
              <span>{p('Mode de prévision par ligne — clic sur le badge pour passer au suivant')}</span>
              <button className="pl-fc-modes-pop__close" onClick={() => setShowModeHelp(false)}>✕</button>
            </div>
            <span className="pl-fc-legend-item"><b>LM</b> {p('Dernier mois — reporte le dernier mois réel sur tous les mois à venir.')}</span>
            <span className="pl-fc-legend-item"><b>MOY</b> {p("Moyenne YTD — moyenne des mois réels de l'année, appliquée aux mois restants.")}</span>
            <span className="pl-fc-legend-item"><b>N-1</b> {p("Saisonnalisé N-1 — reprend le profil mensuel de l'an dernier, recalé sur le niveau réel de l'année.")}</span>
            <span className="pl-fc-legend-item"><b>%</b> {p("% d'une autre ligne — montant dérivé d'une ligne de base (ex. charges sociales = 18 % des salaires).")}</span>
            <span className="pl-fc-legend-item"><b>PP</b> {p('People plan — masse salariale issue des contrats, mouvements et 13')}<sup>e</sup> {p('(lignes de salaire).')}</span>
            <span className="pl-fc-legend-item"><b>—</b> {p('Manuel — aucune génération, saisie libre des mois futurs.')}</span>
          </div>
        </div>
      )}

      {loading && <p className="page-table-loading">{t('common:status.loading')}</p>}

      {!loading && showStructure && (
        <StructureEditor
          sections={sections}
          lines={lines}
          onCreate={handleCreateLine}
          onRename={handleRenameLine}
          onDelete={handleDeleteLine}
          onReorder={handleReorder}
          onRenameSection={handleRenameSection}
        />
      )}

      {!loading && view && (() => {
        const plTable = (
          <PlTable
            view={view}
            thousands={thousands}
            brushMode={brushMode}
            activeLine={activeLine}
            onPickLine={setActiveLine}
            onPaint={handlePaint}
            expanded={expanded}
            onToggleLine={toggleLine}
            expandedSecs={expandedSecs}
            alwaysOpenSections={sectionFilter}
            onToggleSection={toggleSection}
            onAssign={handleAssign}
            editMode={readOnly ? false : editMode}
            readOnly={readOnly}
            onEditValue={handleEditValue}
            onEditForecast={handleEditForecast}
            onFillCs={handleFillCs}
            onFillTax={handleFillTax}
            onClearRate={handleClearRate}
            fcMode={fcMode}
            canForecast={forecastMode && !readOnly}
            actualsOnly={actualsOnly}
            prevView={prevView}
            compareLabel={compareLabel}
            sectionFilter={sectionFilter}
            subtotalFilter={subtotalFilter}
            noCanPct={noCanPct}
            noFold={noFold}
            onSetMethod={handleSetMethod}
            addToast={addToast}
          />
        );
        // Pilote charte : le CR embarqué (hub Rapports → réel ET hub
        // Prévisionnel → forecast) reçoit le gabarit Card partagé « report-card »
        // — corps blanc arrondi + ombre + marge, SANS bande d'en-tête. La card
        // épouse la table (fit-content). Standalone (App.js `profit-loss`,
        // embedded=false) reste nu.
        return embedded
          ? (
            <Card
              className="report-card"
              title={commandBar ? (
                <span className="pl-cmdbar__filters">
                  {[['all', p('Tout')], ['direct', p('Revenus & coûts directs')],
                    ['personnel', p('Personnel')], ['indirect', p('Charges indirectes')]].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className={`pl-fpill${cbFilter === id ? ' is-on' : ''}`}
                        onClick={() => setCbFilter(id)}
                      >
                        {label}
                      </button>
                  ))}
                </span>
              ) : undefined}
              actions={commandBar ? (
                <>
                  <button type="button" className="p-btn pl-tool" onClick={() => applyLevel(expandedSecs.size ? 1 : 3)}>
                    {expandedSecs.size ? p('Tout agréger') : p('Tout déployer')}
                  </button>
                  <button
                    type="button"
                    className={`p-btn pl-tool pl-accordion${cbAccordion ? ' is-on' : ''}`}
                    title={p("Une section à la fois : en ouvrir une ferme les autres")}
                    onClick={() => setCbAccordion(v => !v)}
                  >
                    ▣
                  </button>
                </>
              ) : (<>{plImport}{plTools}</>)}
            >
              {plTable}
            </Card>
          )
          : plTable;
      })()}
    </PageShell>
  );
}

// ── Éditeur de structure (blocs fixes + lignes libres) ──────────────────────
const ROLE_SEMANTIC = {
  salary:         'Salaries',
  bonus:          'Primes & Bonus',
  social_charges: p('Social charges'),
  income_tax:     'Income tax',
};

function StructureEditor({ sections, lines, onCreate, onRename, onDelete, onReorder, onRenameSection }) {
  const { t } = useTranslation();
  const p = useP();
  return (
    <div className="pl-structwrap">
      <div className="pl-map-head">{p('Structure du Compte de résultat — lignes par bloc')}</div>
      <div className="pl-struct-body">
        {sections.map(sec => {
          const secLines = lines.filter(l => l.section === sec.code);
          const ids = secLines.map(l => l.id);
          const move = (i, dir) => {
            const j = i + dir;
            if (j < 0 || j >= ids.length) return;
            const next = ids.slice();
            [next[i], next[j]] = [next[j], next[i]];
            onReorder(next);
          };
          return (
            <div key={sec.code} className="pl-struct-sec">
              <input
                className="pl-struct-sec-h pl-struct-sec-input"
                defaultValue={sec.label}
                title={p('Renommer le bloc')}
                onBlur={e => { const v = e.target.value.trim(); if (v && v !== sec.label) onRenameSection(sec.code, v); }}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              />
              {secLines.map((l, i) => (
                <div key={l.id} className="pl-struct-line">
                  {l.line_role && (
                    <span className="pl-struct-role-tag">{ROLE_SEMANTIC[l.line_role]}</span>
                  )}
                  <input
                    className="pl-struct-input"
                    defaultValue={l.label}
                    onBlur={e => { const v = e.target.value.trim(); if (v && v !== l.label) onRename(l.id, v); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                  />
                  <button className="pl-struct-ic" title="Monter" disabled={i === 0} onClick={() => move(i, -1)}>▲</button>
                  <button className="pl-struct-ic" title="Descendre" disabled={i === secLines.length - 1} onClick={() => move(i, 1)}>▼</button>
                  <button
                    className="pl-struct-ic pl-struct-del"
                    title={l.line_role ? p('Ligne protégée — non supprimable') : p('Supprimer (les comptes seront démappés)')}
                    disabled={!!l.line_role}
                    onClick={() => { if (window.confirm(t('phrases:p_3489f6ac73', { label: l.label }))) onDelete(l.id); }}
                  >×</button>
                </div>
              ))}
              <AddLine onAdd={label => onCreate(sec.code, label)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddLine({ onAdd }) {
  const { t } = useTranslation();
  const p = useP();
  const [val, setVal] = useState('');
  function submit() { const v = val.trim(); if (v) { onAdd(v); setVal(''); } }
  return (
    <div className="pl-struct-add">
      <input
        className="pl-struct-input"
        placeholder={p('+ Ajouter une ligne…')}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
      />
      <button className="pl-struct-addbtn" onClick={submit} disabled={!val.trim()}>{t('common:actions.add')}</button>
    </div>
  );
}

// ── Bouton forecast par ligne (CS / IS) ──────────────────────────────────────
const ROLE_FC_LABEL = { social_charges: p('Charges sociales'), income_tax: p('Impôts') };

function ForecastBtn({ role, year, rate: savedRate, method, tauxConstate, onFillCs, onFillTax, onClear, onConnectPeople, addToast }) {
  const p = useP();
  const [open, setOpen]   = useState(false);
  const [rate, setRate]   = useState('');
  const [busy, setBusy]   = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  async function apply() {
    const v = parseFloat(String(rate).replace(',', '.'));
    if (!Number.isFinite(v) || v <= 0 || v > 100) {
      addToast('Taux invalide (0–100 %).', 'error');
      return;
    }
    setBusy(true);
    try {
      const taux = v / 100;
      if (role === 'social_charges') await onFillCs({ year, taux });
      else if (role === 'income_tax') await onFillTax({ year, taux });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  // Même grammaire visuelle que les autres lignes (MethodChip) : un sélecteur
  // bleu — Manuel · Taux (%) · Plan personnel (People) sur la ligne CS. Le
  // popup ne sert plus qu'à SAISIR le taux (décision design 2026-08-20).
  const value = method === 'people_plan' ? 'people_plan' : (savedRate != null ? 'rate' : '');
  const openRate = () => {
    const seed = savedRate != null ? savedRate : tauxConstate;
    setRate(seed != null ? String(Math.round(seed * 1000) / 10) : '');
    setOpen(true);
  };
  async function handleChange(e) {
    const v = e.target.value;
    if (v === 'people_plan' && onConnectPeople) {
      setBusy(true);
      try { await onConnectPeople(); } finally { setBusy(false); }
    } else if (v === 'rate') openRate();
    else if (v === '' && onClear) onClear();
  }

  return (
    <div className="pl-fc-wrap" ref={ref}>
      <select
        className={`pl-fc-select${value ? '' : ' pl-fc-select--man'}`}
        value={value}
        disabled={busy}
        onChange={handleChange}
        title={ROLE_FC_LABEL[role]}
      >
        <option value="">{p('Manuel')}</option>
        <option value="rate">{role === 'income_tax' ? p('Taux sur RAI') : p('Taux sur salaires')}</option>
        {role === 'social_charges' && onConnectPeople && (
          <option value="people_plan">{p('Plan personnel (People)')}</option>
        )}
      </select>
      {value === 'rate' && (
        <button className="pl-fc-edit" title={p('Configurer')} onClick={openRate}>
          {fmtPctShort(savedRate)}
        </button>
      )}
      {open && (
        <div className="pl-fc-pop">
          <div className="pl-fc-pop-title">{ROLE_FC_LABEL[role]}</div>
          <div className="pl-fc-constate">
            {role === 'income_tax'
              ? p('% appliqué au résultat avant impôts.')
              : p('% appliqué aux salaires + primes.')}
          </div>
          {role === 'social_charges' && tauxConstate != null && (
            <div className="pl-fc-constate">{p('Constaté : ')}{(tauxConstate * 100).toFixed(1)} %</div>
          )}
          <div className="pl-fc-pop-row">
            <input
              className="pl-fc-input"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder={role === 'social_charges' ? '18.5' : '8.5'}
              value={rate}
              onChange={e => setRate(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') setOpen(false); }}
              autoFocus
            />
            <span className="pl-fc-pct">%</span>
          </div>
          <button className="pl-fc-apply" onClick={apply} disabled={busy || !rate}>
            {busy ? '…' : p('Appliquer')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Badge mode de forecast (clic = mode suivant) ─────────────────────────────
const fmtPctShort = r => {
  const v = r * 100;
  return (Number.isInteger(v) ? v : v.toFixed(1)) + '%';
};

// Modes de prévision proposés dans la liste déroulante (par ligne).
// Les CONNEXIONS à un autre module sont des méthodes comme les autres, offertes
// là où elles ont un sens : plan personnel sur les salaires, plan des ventes sur
// les lignes de revenus. Un interrupteur global « connecter Finance à People /
// aux Ventes » créerait un second endroit pour décider la même chose — et la
// connexion serait tout ou rien, alors qu'ici elle se choisit ligne par ligne.
/* LE SALAIRE N'A QUE TROIS MODES (2026-09-18) : dernier mois, cible, manuel.
   Une moyenne à date, une saisonnalité de l'an dernier ou un pourcentage d'une
   autre ligne ne disent rien d'une masse salariale — elle se reconduit, elle se
   vise, ou elle se tape.
   ⚠️ « Plan personnel (People) » n'est PAS de la liste : c'est l'interrupteur
   « Personnel : Connecté » qui branche la ligne sur le plan, et il la verrouille
   alors. Deux chemins pour une même connexion, dont un qui ne dit pas qu'il
   verrouille, laissaient croire à deux états différents. */
const SALARY_METHODS = new Set(['', 'last_actual', 'target']);
/* LES AUTRES CHARGES DE PERSONNEL en gardent deux de plus : une moyenne à date
   a du sens sur des primes ou des frais, qui se répètent sans se reconduire à
   l'identique, et « vide » sert à éteindre une ligne qu'on ne prévoit pas.
   (Les charges sociales n'ont pas cette liste : elles ont leur bouton de taux.) */
const PERSONNEL_METHODS = new Set([...SALARY_METHODS, 'avg_ytd', 'zero']);

const FC_OPTIONS = (isSalary, isRevenue, isPurchases, isCogs, isStockVar, method = '', isPersonnel = false, isOpex = false) => [
  { v: '', l: p('Manuel') },
  { v: 'last_actual', l: p('Dernier mois') },
  { v: 'avg_ytd', l: p('Moyenne YTD') },
  { v: 'n1_seasonal', l: p('Saisonnalisé N-1') },
  { v: 'pct_of', l: p("% d'une ligne") },
  { v: 'target', l: 'Cible' },
  // Connexion au module Ventes (2026-09-12) : « Plan des ventes » remplace les
  // règles liées aux cards. Un compte de CA lit le Plan mensuel, une charge
  // directe le coût des produits vendus du plan ; la ligne se lit, calculée.
  // Un compte de CA ne se propose plus « Plan des ventes » (2026-10-22) : la ligne lit le plan d'elle-même dès qu'une offre
  // porte ce compte ; une règle déjà posée reste lisible, pour qu'on puisse s'en défaire.
  ...((isRevenue ? method === 'sales_offers' : (isCogs && !isStockVar)) ? [{ v: 'sales_offers', l: p('Plan des ventes') }] : []),
  // Une règle d'avant (cards) reste lisible tant qu'on ne l'a pas changée.
  ...(['sales_plan', 'sales_purchases', 'sales_cards', 'sales_cogs'].includes(method) ? [{ v: method, l: p('Ventes & Ops (ancien)') }] : []),
  // LES CHARGES FIXES (2026-09-29) : un compte d'exploitation lit ce que ses
  // charges fixes prévoient (Ventes & Ops › Opérations › Charges fixes).
  ...(isOpex || method === 'fixed_charges' ? [{ v: 'fixed_charges', l: p('Charges fixes') }] : []),
  { v: 'zero', l: p('Vide (0)') },
].filter((o) => {
  if (o.v === method) return true;                       // la règle en place reste lisible
  if (isSalary) return SALARY_METHODS.has(o.v);
  if (isPersonnel) return PERSONNEL_METHODS.has(o.v);
  return true;
});

/* `revenueBase` (Ventes & Ops › Coûts variables, 2026-09-29) : le « % d'une
   ligne » devient « % des revenus », sa base se prend parmi les lignes de
   produits ou le chiffre d'affaires net entier, et le taux se tape positif
   pour une charge (5 % du chiffre d'affaires), enregistré négatif. */
const REVENUE_BASE = 'sec:revenue';

export function MethodChip({ method, rate, baseId, target, isSalary, isPersonnel = false, isRevenue, isPurchases, isCogs, isStockVar, isOpex = false, lines, selfId, onSet, choices = null, revenueBase = false }) {
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgKind, setCfgKind] = useState('pct');     // 'pct' | 'target'
  const [cfgRate, setCfgRate] = useState('');
  const [cfgBase, setCfgBase] = useState('');
  const [cfgTarget, setCfgTarget] = useState('');
  // Hors du compte de résultat, la fenêtre s'ouvre au-dessus de la page : une
  // carte de deux lignes la couperait à son bord.
  const [popPos, setPopPos] = useState(null);
  const ref = useRef();

  useEffect(() => {
    if (!cfgOpen) return;
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setCfgOpen(false); }
    function onScroll() { setCfgOpen(false); }
    document.addEventListener('mousedown', close);
    if (revenueBase) window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [cfgOpen, revenueBase]);

  function placePop() {
    if (revenueBase && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPopPos({ position: 'fixed', top: r.bottom + 4, left: r.left, right: 'auto' });
    }
  }
  function openPct() {
    placePop();
    setCfgKind('pct');
    setCfgRate(rate != null ? String(Math.round((revenueBase ? Math.abs(rate) : rate) * 1000) / 10) : '');
    setCfgBase(baseId || '');
    setCfgOpen(true);
  }
  function openTarget() {
    placePop();
    setCfgKind('target');
    setCfgTarget(target != null ? String(Math.round(revenueBase ? Math.abs(target) : target)) : '');
    setCfgOpen(true);
  }
  function handleChange(e) {
    const v = e.target.value || null;
    if (v === 'pct_of') { onSet({ forecast_method: 'pct_of' }); openPct(); }
    else if (v === 'target') { onSet({ forecast_method: 'target' }); openTarget(); }
    else { setCfgOpen(false); onSet({ forecast_method: v }); }
  }

  function applyPct() {
    const v = parseFloat(String(cfgRate).replace(',', '.'));
    if (!Number.isFinite(v) || v === 0 || Math.abs(v) > 100 || !cfgBase) return;
    onSet({
      forecast_method: 'pct_of',
      forecast_rate: revenueBase && !isRevenue ? -Math.abs(v) / 100 : v / 100,
      forecast_base_line_id: cfgBase,
      set_forecast_params: true,
    });
    setCfgOpen(false);
  }

  function applyTarget() {
    const v = parseFloat(String(cfgTarget).replace(/[\s']/g, '').replace(',', '.'));
    if (!Number.isFinite(v)) return;
    // Hors du compte de résultat, une charge se vise en positif, enregistrée négative.
    onSet({ forecast_method: 'target', forecast_target: revenueBase && !isRevenue ? -Math.abs(v) : v, set_forecast_params: true });
    setCfgOpen(false);
  }

  /* « % des revenus » posé (2026-09-29) : la liste montre le COMPTE de base,
     le bouton le seul taux (« Revenus des prestations » puis « 30% »). */
  const baseName = baseId === REVENUE_BASE ? p("Chiffre d'affaires net") : ((lines || []).find((l) => l.id === baseId)?.label || '');
  const paramLabel = method === 'pct_of'
    ? (rate != null ? fmtPctShort(revenueBase ? Math.abs(rate) : rate) : '%')
    : (method === 'target' ? (target != null ? `${Math.round(revenueBase ? Math.abs(target) : target).toLocaleString('de-CH')}` : 'cible') : null);

  return (
    <div className="pl-fc-wrap" ref={ref}>
      <select
        className={`pl-fc-select${method ? '' : ' pl-fc-select--man'}`}
        value={method || ''}
        onChange={handleChange}
      >
        {/* La règle en place reste lisible, même hors des choix proposés. */}
        {FC_OPTIONS(isSalary, isRevenue, isPurchases, isCogs, isStockVar, method || '', isPersonnel, isOpex)
          .filter((o) => !choices || choices.includes(o.v) || o.v === (method || ''))
          .map(o => <option key={o.v} value={o.v}>{o.v === 'pct_of' && revenueBase
            ? (method === 'pct_of' && baseName ? baseName : p('% des revenus')) : o.l}</option>)}
      </select>
      {paramLabel != null && (
        <button className="pl-fc-edit" title={p('Configurer')}
                onClick={() => (method === 'pct_of' ? openPct() : openTarget())}>
          {paramLabel}
        </button>
      )}
      {cfgOpen && cfgKind === 'pct' && (
        <div className="pl-fc-pop" style={revenueBase ? popPos || undefined : undefined}>
          <div className="pl-fc-pop-title">{revenueBase ? p('% des revenus') : p("% d'une autre ligne")}</div>
          <select
            className="pl-fc-base"
            value={cfgBase}
            onChange={e => setCfgBase(e.target.value)}
          >
            <option value="">{p('Ligne de base…')}</option>
            <option value={REVENUE_BASE}>{p("Chiffre d'affaires net (CAN)")}</option>
            {lines.filter(l => l.id !== selfId && (!revenueBase || l.section === 'revenue')).map(l => (
              <option key={l.id} value={l.id}>{revenueBase ? l.label : `${l.section_label} · ${l.label}`}</option>
            ))}
          </select>
          <div className="pl-fc-pop-row">
            <input
              className="pl-fc-input"
              type="number"
              step="5"
              min="-100"
              max="100"
              placeholder={revenueBase ? '5' : '-80'}
              value={cfgRate}
              onChange={e => setCfgRate(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyPct(); if (e.key === 'Escape') setCfgOpen(false); }}
            />
            <span className="pl-fc-pct">%</span>
          </div>
          {!revenueBase && <div className="pl-fc-constate">{p('Forecast = base × taux. Taux négatif pour une charge calculée sur un produit (ex. −80).')}</div>}
          <button className="pl-fc-apply" onClick={applyPct} disabled={!cfgBase || !cfgRate}>{p('Appliquer')}</button>
        </div>
      )}
      {cfgOpen && cfgKind === 'target' && (
        <div className="pl-fc-pop" style={revenueBase ? popPos || undefined : undefined}>
          <div className="pl-fc-pop-title">Cible — total annuel</div>
          <div className="pl-fc-pop-row">
            <input
              className="pl-fc-input"
              type="text"
              inputMode="decimal"
              placeholder="6 000 000"
              value={cfgTarget}
              onChange={e => setCfgTarget(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyTarget(); if (e.key === 'Escape') setCfgOpen(false); }}
              autoFocus
            />
            <span className="pl-fc-pct">CHF</span>
          </div>
          {!revenueBase && <div className="pl-fc-constate">{p('Les mois ouverts sont remplis pour atteindre la cible (− le réel déjà saisi), répartis selon la saisonnalité N-1. Charge = cible négative.')}</div>}
          <button className="pl-fc-apply" onClick={applyTarget} disabled={!cfgTarget}>{p('Appliquer')}</button>
        </div>
      )}
    </div>
  );
}

// ── Table P&L (Reporting Line · N° · Désignation · Total · jan→déc) ──────────
// Cellule numérique éditable (mode déverrouillé).
function InlineNum({ value, onSave, forecast = false, thousands = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef();
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select(); } }, [editing]);
  // Saisie dans l'unité active : en kCHF on saisit des kCHF (×1000 au stockage),
  // en CHF on saisit des CHF — même logique que le Bilan.
  function start(e) { e.stopPropagation(); setDraft(value ? String(thousands ? Math.round(value / 1000) : value) : ''); setEditing(true); }
  function commit() {
    setEditing(false);
    const parsed = parseFloat(String(draft).trim().replace(/[\s']/g, '').replace(',', '.')) || 0;
    const v = thousands ? Math.round(parsed * 1000) : parsed;
    if (v !== value) onSave(v);
  }
  if (editing) {
    return (
      <input
        ref={ref}
        className="pl-edit-input"
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') setEditing(false);
          if (e.key === 'Tab') {
            // Tab = valider et ouvrir la cellule éditable suivante (Shift+Tab : précédente)
            // de la même ligne.
            e.preventDefault();
            const td = ref.current?.closest('td');
            commit();
            let n = e.shiftKey ? td?.previousElementSibling : td?.nextElementSibling;
            while (n) {
              const span = n.querySelector('.pl-edit-cell');
              if (span) { span.click(); break; }
              n = e.shiftKey ? n.previousElementSibling : n.nextElementSibling;
            }
          }
        }}
      />
    );
  }
  return (
    <span
      className={`pl-edit-cell${forecast ? ' pl-edit-cell--forecast' : ''}`}
      onClick={start}
      title={forecast ? p('Forecast — cliquer pour saisir') : p('Cliquer pour modifier')}
    >{chf(value, thousands)}</span>
  );
}

// Sous-totaux pour lesquels on affiche un % de marge (/ revenus).
/* Sous-lignes de pourcentage du prévisionnel. « % Résultat net » retiré le
   2026-08-16 : la colonne % CAN du bloc cumulé porte déjà l'information, et la
   ligne fermait la table sur une rangée de peu de valeur. */
const PCT_ROWS = {
  value_added:  p('% Valeur ajoutée'),
  gross_margin: '% Marge brute',
  ebit:         '% EBIT',
};

// Réel seul (États financiers) : sous-ligne « en % du CA net » sous ces seuls
// indicateurs (le cumul % CAN est déjà porté par la colonne de droite).
const PCT_OF_CAN = ['value_added', 'gross_margin'];

// ── Cible « Coût des ventes » — driver de la variation de stock prévisionnelle.
// Trois modes : % de lignes de revenu (N lignes × N taux) · valeur fixe (CHF/mois)
// · cible annuelle (CHF/an). Popover d'édition, sauvegarde en forecast_assumption.
// Niveau de lecture (bouton cyclique) : 1 synthétique · 2 ouvert (lignes) ·
// 3 tout ouvert (comptes). L'icône suggère le degré de dépliage.
const LEVEL_LABELS = { 1: p('⊟ Synthétique'), 2: p('⊡ Ouvert'), 3: p('⊞ Tout ouvert') };

export function PlTable({ view, thousands, brushMode, activeLine, onPickLine, onPaint, expanded, onToggleLine,
                  expandedSecs, alwaysOpenSections = null, onToggleSection, onAssign, editMode, onEditValue, onEditForecast,
                  onFillCs, onFillTax, onClearRate, fcMode, canForecast, actualsOnly, readOnly = false, prevView, compareLabel = null, sectionFilter, subtotalFilter, noCanPct = false, noFold = false, onSetMethod, addToast, monthsFull, noPct }) {
  // Filtre par section (re-découpage thématique du forecast : CA, personnel…).
  // null = vue complète. Les sous-totaux sont restreints à `subtotalFilter`.
  const { t } = useTranslation();
  const p = useP();
  const nav = useContext(ReportNavContext);
const secOK = (code) => !sectionFilter || sectionFilter.includes(code);
  const subOK = (code) => !sectionFilter || (subtotalFilter || []).includes(code);
  const fmt = v => chf(v, thousands);
  const closedCount = Number.isFinite(view.closed_count) ? view.closed_count : 12;
  // Vue réel seul (États financiers) : on borne l'affichage aux mois clôturés
  // et on masque la colonne Total FY (qui inclut la prévision). `monthsFull`
  // (impression) force les 12 mois pour une mise en page stable, mois vides = «–».
  // noPct (impression) : on garde les mois mais on retire les colonnes % (% CAN,
  // Δ %) du bloc cumulé — gain de place.
  const visCount = (actualsOnly && !monthsFull) ? Math.max(closedCount, 0) : 12;
  const monthLabels = MONTHS.slice(0, visCount);
  // Colonnes de synthèse (mode forecast) adaptées au statut de l'année vs clôture :
  // - « Total cumulé (réel) » n'a de sens que si l'année a des mois réels ;
  // - « Total (réel + prév.) » n'a de sens que s'il reste des mois en prévision.
  // Année en cours → les deux · N+1 (tout prévision) → pas de colonne réel ·
  // N-1 (tout réel) → pas de colonne prévision.
  const showYtd = closedCount > 0;
  const showFy  = closedCount < 12;
  const months = m => monthLabels.map((_, i) => Number(m[String(i + 1)] || 0));
  // Comparatif réel seul : cumul N-1 sur la même période (mois 1..closedCount).
  const prevYear = (view.year ?? 0) - 1;
  // Intitulé de la colonne comparative : « Exercice N-1 » ou le nom de la
  // version figée à laquelle on se compare.
  const cmpTitle = compareLabel || `Exercice ${prevYear} · N-1`;
  const cmpShort = compareLabel || prevYear;
  const priorByCode = {};
  if (actualsOnly && prevView) {
    (prevView.rows || []).forEach(r => {
      let s = 0;
      for (let m = 1; m <= closedCount; m++) s += Number(r.months?.[String(m)] || 0);
      priorByCode[r.code] = s;
    });
  }
  // Total année pleine N-1 par code (prévisionnel) — colonne Total N-1.
  const prevTotalByCode = {};
  if (prevView) (prevView.rows || []).forEach(r => { prevTotalByCode[r.code] = Number(r.total || 0); });
  const isOpen = code => expanded.has(code);
  /* Une section FILTRÉE s'ouvre par défaut, mais elle se REPLIE (2026-09-18) :
     elle était forcée ouverte, au motif qu'une étape se réduirait à une ligne.
     Sur un onglet qui en porte deux — revenus et charges directes — on veut
     pourtant refermer l'une pour lire l'autre. Le défaut suffit : on ouvre ce
     qui est le sujet, on laisse refermer. */
  /* `expandedSecs` porte les BASCULES de l'utilisateur ; une section filtrée
     part ouverte, les autres fermées. Un clic inverse donc son état de départ,
     ce qui laisse replier l'une pour lire l'autre. */
  const filterOpen = new Set(alwaysOpenSections || []);
  /* `noFold` (2026-10-03) : sur une vue qui ne montre qu'un bloc (Personnel,
     Autres charges), replier n'a pas de sens ; la section reste ouverte et
     son chevron disparaît. */
  const isSecOpen = (code) => (noFold || (expandedSecs.has(code) !== filterOpen.has(code)));
  const secHasLines = new Set(view.rows.filter(r => r.type === 'detail').map(r => r.section));
  /* Sous-total de GROUPE remonté EN TÊTE de ses lignes (2026-08-17) : le
     chevron s'ouvre vers le bas, comme toutes les autres rubriques du compte
     de résultat — un total placé sous ses lignes lisait à l'envers. La carte
     Coûts directs (Ventes & Ops) garde sa présentation, sous-total dessous. */
  const orderedRows = (() => {
    const out = [];
    const pending = [];
    view.rows.forEach((r) => {
      if (r.type === 'subtotal' && (r.code === 'cogs_goods' || r.code === 'cogs_other')) {
        // Ses lignes ont été mises de côté juste avant : on pose l'en-tête, puis elles.
        out.push(r, ...pending.splice(0));
        return;
      }
      if (r.type === 'detail' && (r.group === 'cogs_goods' || r.group === 'cogs_other')) {
        pending.push(r);
        return;
      }
      out.push(...pending.splice(0), r);
    });
    return out.concat(pending);
  })();
  const lineRoleMap = Object.fromEntries((view.lines || []).map(l => [l.id, l.line_role]));
  const lineFcMap   = Object.fromEntries((view.lines || []).map(l => [l.id, l]));
  // Sous-traitance hors stock (cas KeyIT) : sort du groupe, rendue sous le Coût
  // des ventes (à sa place normale = sautée).
  const subRow = view.rows.find(r => r.type === 'detail' && r.line_role === 'subcontracting');
  const subOutOfGroup = !!(canForecast && subRow && !subRow.stock_linked);
  const [dragAcc, setDragAcc]   = useState(null);   // compte en cours de glissement
  // Groupes de charges directes repliés (chevron sur l'en-tête de groupe) :
  // un client à nombreux comptes lit d'abord les deux totaux (2026-08-16).
  const [foldedGroups, setFoldedGroups] = useState(() => new Set());
  const toggleGroup = (code) => setFoldedGroups((s0) => {
    const n = new Set(s0); if (n.has(code)) n.delete(code); else n.add(code); return n;
  });
  const [overCode, setOverCode] = useState(null);   // cible survolée (line id | 'unmapped')

  // Revenus totaux (dénominateur du %) — section_total revenue.
  const revenueRow = view.rows.find(r => r.type === 'section_total' && r.section === 'revenue');
  const revMonths  = revenueRow ? months(revenueRow.months) : Array(visCount).fill(0);
  const revYtd     = revenueRow?.total_ytd || 0;
  const revFy      = revenueRow?.total     || 0;
  const prevRevYtd = priorByCode['sec:revenue'] || 0;   // CA net cumulé N-1 (dénom. % N-1)
  function pct(v, rev) {
    if (!rev) return '–';
    return (v / rev * 100).toFixed(1) + ' %';
  }
  // % du chiffre d'affaires net, vide si pas de valeur (pas de 0 % trompeur).
  const pctCan = (v, rev) => (v == null || !rev) ? '' : (v / rev * 100).toFixed(1) + ' %';

  // Borne actual / forecast : mois 1..closedCount = réels · au-delà = forecast.
  const isForecast = i => (i + 1) > closedCount;   // i = index 0-based du mois

  // Classes de cellule chiffrée : négatif → terracotta (charte : charges/négatif).
  // Bord vertical net au 1ᵉʳ mois prévisionnel — frontière réel | prévision.
  const fcEdge = (i) => (closedCount > 0 && closedCount < 12 && i === closedCount) ? ' pl-fcedge' : '';
  const nCls = (v, i) => `pl-num${isForecast(i) ? ' pl-mforecast' : ''}${v < 0 ? ' pl-neg' : ''}${fcEdge(i)}`;
  const ytdCls = v => `pl-num pl-num--total pl-num--ytd${v < 0 ? ' pl-neg' : ''}`;
  const fyCls  = v => `pl-num pl-num--fy${v < 0 ? ' pl-neg' : ''}`;
  // Colonnes de fin. Mode complet : Total cumulé + Total FY (année pleine).
  // Réel seul : Total cumulé + N-1 (même période) + variance absolue + %.
  // Le cumul des mois clos se lit JUSTE APRÈS le dernier mois réalisé (2026-08-16)
  // — il ferme la zone « Réalisé » au lieu d'attendre le bout de la table.
  const ytdInline = showYtd && !actualsOnly && closedCount > 0 && closedCount < 12;
  /* LES DOUZE MOIS SE SUIVENT, LE BLOC ANNUEL FERME LA TABLE (2026-09-18).
     Le cumul des mois clos et son % se lisaient AU MILIEU, juste après le
     dernier mois réalisé : l'année se coupait en deux, la colonne changeait de
     place d'un client à l'autre selon l'avancement de la clôture, et comparer
     un mois d'une étape à l'autre demandait de recompter. On lit donc janvier à
     décembre d'affilée, puis le Total de l'exercice, ce qui est RÉALISÉ dedans,
     sa part du chiffre d'affaires, et ce qui reste À VENIR.
     L'ordre des colonnes annuelles est décrit UNE fois : l'entête, les lignes,
     les sous-totaux et les lignes de pourcentage le lisent tous ici, sans quoi
     une colonne ajoutée quelque part décalerait les autres. */
  /* `noCanPct` (2026-09-19) : une vue qui ne montre QUE les revenus n'a pas
     besoin de la part de chaque ligne dans le chiffre d'affaires — la table
     entière est ce chiffre d'affaires. La colonne reste partout ailleurs. */
  const annCols = [
    showFy && 'fy',
    !ytdInline && showYtd && 'ytd',
    ytdInline && !noCanPct && 'pct',
  ].filter(Boolean);
  /* LE TOTAL RÉALISÉ SUIT LE DERNIER MOIS CLOS, « TOTAL À VENIR » SUIT DÉCEMBRE (demande d'Olivier, 2026-10-22) : la
     zone Réalisé se ferme par son total, la zone Prévisionnel par le sien ; le bloc annuel ne garde que le Total de
     l'exercice et sa part du chiffre d'affaires (% CAN, celui du Total seulement). `inl` insère ces deux cellules
     dans chaque rangée, à la bonne place : l'entête, les lignes, les sous-totaux, les ajustements et les lignes de
     pourcentage l'appellent tous. */
  const inl = (cells, ytdNode, togoNode) => (ytdInline
    ? [...cells.slice(0, closedCount), ytdNode, ...cells.slice(closedCount), togoNode]
    : cells);
  const ytdTd = (ytd) => <td key="ytd-in" className={`${ytdCls(ytd)} pl-ytd-inline`}>{fmt(ytd)}</td>;
  const togoTd = (fy, ytd) => {
    const togo = fy - ytd;
    return <td key="togo-in" className={`pl-num pl-num--togo pl-togo-inline${togo < 0 ? ' pl-neg' : ''}`}>{fmt(togo)}</td>;
  };
  const blankYtd = (cls = '') => <td key="ytd-in" className={`pl-num pl-ytd-inline${cls}`} />;
  const blankTogo = (cls = '') => <td key="togo-in" className={`pl-num pl-togo-inline${cls}`} />;
  const annCls = (id, extra = '') => {
    const i = annCols.indexOf(id);
    return `pl-annual${i === 0 ? ' pl-annual--first' : ''}`
      + `${i === annCols.length - 1 ? ' pl-annual--last' : ''}${extra ? ` ${extra}` : ''}`;
  };
  const tailCells = (ytd, fy, code) => {
    const prior = (actualsOnly && code != null && code in priorByCode) ? priorByCode[code] : null;
    const dv = prior == null ? null : ytd - prior;
    if (!actualsOnly) {
      const prevTot = (code != null && code in prevTotalByCode) ? prevTotalByCode[code] : null;
      const ec = prevTot == null ? null : fy - prevTot;
      const ecCls = ec == null ? '' : (ec > 0 ? ' pl-pos' : ec < 0 ? ' pl-neg' : '');
      const dpct = (prevTot == null || !prevTot) ? null : (ec / Math.abs(prevTot)) * 100;
      return (<>
        {showFy && <td className={`${fyCls(fy)} ${annCls('fy')}`}>{fmt(fy)}</td>}
        {!ytdInline && showYtd && <td className={`${ytdCls(ytd)} ${annCls('ytd')}`}>{fmt(ytd)}</td>}
        {ytdInline && !noCanPct && (
          <td className={`pl-num ${annCls('pct', 'pl-annual-pct')}${fy < 0 ? ' pl-neg' : ''}`}>
            {pctCan(fy, revFy)}
          </td>
        )}
        {prevView && (
          <td className={`pl-num pl-col-fy pl-blk2025${prevTot != null && prevTot < 0 ? ' pl-neg' : ''}`}>
            {prevTot == null ? '' : fmt(prevTot)}
          </td>
        )}
        {prevView && (
          <td className={`pl-num pl-ecart-chf${ecCls}`}>{ec == null ? '' : fmt(ec)}</td>
        )}
        {prevView && (
          <td className={`pl-num pl-ecart-pct${ecCls}`}>
            {dpct == null ? '' : `${(Math.round(dpct * 10) / 10).toLocaleString('de-CH')} %`}
          </td>
        )}
      </>);
    }
    const eCls = dv == null ? '' : (dv > 0 ? ' pl-pos' : dv < 0 ? ' pl-neg' : '');
    // Impression sans % : bloc cumulé réduit aux montants CHF (N · N-1 · Écart).
    if (noPct) {
      return (<>
        <td className="pl-num pl-cumul-chf">{fmt(ytd)}</td>
        <td className="pl-spacer" />
        <td className="pl-num pl-blk2025 pl-2025-chf">{prior == null ? '' : fmt(prior)}</td>
        <td className="pl-spacer" />
        <td className={`pl-num pl-ecart-chf${eCls}`}>{dv == null ? '' : fmt(dv)}</td>
        <td className={`pl-num pl-ecart-pct${eCls}`}>
          {(dv == null || !prior) ? '' : `${(dv / Math.abs(prior) * 100).toFixed(1)} %`}
        </td>
      </>);
    }
    // % : couleur de la ligne par défaut, terracotta seulement si négatif.
    const cumPctNeg = ytd < 0 ? ' pl-neg' : '';
    const prPctNeg = (prior != null && prior < 0) ? ' pl-neg' : '';
    return (<>
      {/* espace */}<td className="pl-spacer" />
      {/* Cumulé N — CHF (couleur de ligne) + % CAN */}
      <td className="pl-num pl-cumul-chf">{fmt(ytd)}</td>
      <td className={`pl-num pl-cumul-pct${cumPctNeg}`}>{pctCan(ytd, revYtd)}</td>
      {/* espace */}<td className="pl-spacer" />
      {/* Exercice N-1 : CHF + Écart + Delta % (le % CAN 2025 est retiré à l'écran) */}
      <td className="pl-num pl-blk2025 pl-2025-chf">{prior == null ? '' : fmt(prior)}</td>
      <td className={`pl-num pl-ecart-chf${eCls}`}>{dv == null ? '' : fmt(dv)}</td>
      <td className={`pl-num pl-ecart-pct${eCls}`}>{(dv == null || !prior) ? '' : (dv / Math.abs(prior) * 100).toFixed(1) + ' %'}</td>
    </>);
  };

  // Cible de dépôt (drag-and-drop) : assigne le compte glissé à `target`
  // (line id, ou null pour démapper via la zone « Non mappé »).
  function dropProps(targetCode, lineIdOrNull) {
    return {
      onDragOver: e => { if (dragAcc) { e.preventDefault(); setOverCode(targetCode); } },
      onDragLeave: () => setOverCode(c => (c === targetCode ? null : c)),
      onDrop: e => {
        e.preventDefault();
        if (dragAcc) onAssign(dragAcc, lineIdOrNull);
        setDragAcc(null); setOverCode(null);
      },
    };
  }

  // Ligne de reporting (en-tête de groupe) : repli/dépli + pinceau + cible DnD.
  function lineRow(row) {
    const hasAccts = row.accounts.length > 0;
    const hasAdjs  = (row.adjustments || []).length > 0;
    const canExpand = hasAccts || hasAdjs;
    const open = isOpen(row.code);
    const pickable = brushMode;
    const active = brushMode && activeLine === row.code;
    const role = lineRoleMap[row.code];
    // Groupe stock : Achats · Sous-traitance (si liée au stock) · Variation. La
    // sous-traitance hors stock sort du groupe (rendue sous le Coût des ventes).
    // Affordance PRÉVISIONNELLE uniquement (liseré de regroupement + icône ↔ « lié
    // au stock ») : en vue réelle (Rapports, PDF), ce serait du bruit —
    // on la masque hors mode forecast.
    // Variation de stock DÉRIVÉE = pilotée par la cible « Coût des ventes »
    // (lecture seule, pas de saisie ni de mode manuel).
    const isDerivedVar = false;   // cible de coût des ventes retirée (2026-08-16)
    // Amortissement des nouveaux actifs = CALCULÉ (taux × brut, prorata) → lecture
    // seule, jamais éditable ni bleu.
    const isPlanAmort = row.code === 'plan_amort' || role === 'planned_amort';
    // Lignes POSSÉDÉES par Ventes & Opérations (Coûts produits & stock) : les
    // lignes marquées « coût produits » toujours ; Achats et Variation dès que
    // la section est engagée (plan de stock, cible de consommation ou marquage)
    // — sinon un client sans le module perdrait toute saisie (pas de porte morte).
    const pcpCost = (view.product_cost_plan && view.product_cost_plan.cost_lines) || [];
    const sopEngaged = !!(view.stock_plan || pcpCost.length);
    const isSopOwned = canForecast && (pcpCost.includes(row.code)
      || (sopEngaged && (role === 'purchases' || role === 'stock_variation')));
    // SOURCE de la ligne (décision Olivier 2026-08-16) : c'est la MÉTHODE qui
    // désigne qui produit les chiffres. « Plan des ventes » (et ses variantes
    // négoce / coût des ventes) → l'écran Valeur ajoutée planifiée ; toute
    // autre méthode → Finance. On ne saisit pas chez le voisin, mais la
    // méthode reste modifiable ici : c'est de Finance qu'on branche ou
    // débranche une ligne du module Ventes & Opérations.
    const isVaPlanned = canForecast
      && ['sales_plan', 'sales_cogs', 'sales_purchases', 'sales_offers'].includes(lineFcMap[row.code]?.forecast_method);
    // Lue du plan des ventes : un compte porté par une offre se lit, calculé là-bas.
    const isSalesLocked = canForecast && !!row.sales_locked;
    // Sous le plan des ventes, Variation de stock se TAPE ; sa
    // règle n'a plus cours, le coût des produits vendus venant du plan (2026-09-12).
    const isManualOnly = canForecast && !!row.manual_only;
    // Portée par une offre, la ligne n'a plus de règle à choisir, quelle que soit celle qu'elle porte (2026-10-22) ;
    // branchée par la seule règle « Plan des ventes », la règle reste changeable.
    const isSalesGlobal = isSalesLocked && (!!row.offer_locked || lineFcMap[row.code]?.forecast_method !== 'sales_offers');
    const isPeopleLocked = canForecast && !!row.people_locked;
    /* Le PARC EXISTANT s'amortit selon le Plan d'investissement (2026-09-19,
       soir) : la ligne porte la charge du plan et ne se règle plus ici. */
    const isCapexLocked = canForecast && !!row.capex_locked;
    /* LE BUDGET DES CHARGES (2026-09-28) : un compte porté par au moins une
       ligne du budget s'en lit, et ne se règle plus ici. */
    const isBudgetLocked = canForecast && !!row.budget_locked;
    // Ligne calculée par un schéma de Trésorerie (reprise d'acomptes clients, 2026-09-13).
    const isComputedLine = canForecast && !!row.computed;
    const isCalc = isDerivedVar || isPlanAmort || isSopOwned || isVaPlanned || isSalesLocked || isPeopleLocked || isCapexLocked || isBudgetLocked || isComputedLine;
    const rowClick = pickable
      ? () => onPickLine(row.code)
      : (canExpand ? () => onToggleLine(row.code) : undefined);
    return (
      <tr
        className={`pl-row pl-row--line${row.group ? ' pl-row--ingroup' : ''}${pickable ? ' pl-pickable' : ''}${active ? ' pl-brush-active' : ''}${canExpand && !pickable ? ' pl-collapsible' : ''}${overCode === row.code ? ' pl-droptarget' : ''}`}
        onClick={rowClick}
        {...dropProps(row.code, row.code)}
      >
        <td className="pl-line">
          <span className="pl-caret" onClick={canExpand ? e => { e.stopPropagation(); onToggleLine(row.code); } : undefined}>
            {canExpand ? <PlChevron open={open} /> : null}
          </span>
          {active ? '🖌 ' : ''}
          {row.label}
          {isDerivedVar && <span className="pl-calc-badge" title={p('Calculée = Coût des ventes − Achats − Sous-traitance')}>{p('calculée')}</span>}
          {/* LES AMORTISSEMENTS ONT LEURS ÉCRANS (2026-10-03) : le badge dit
              lequel, la ligne ne porte plus de règle. */}
          {isPlanAmort && <span className="pl-calc-badge pl-src" title={p('Se planifie dans Ventes & Ops / Acquisitions')}>{p('Ventes & Ops / Acquisitions')}</span>}
          {isSopOwned && !isDerivedVar && !isVaPlanned && !isSalesLocked && fcMode && (
            <span className="pl-calc-badge pl-src" title={p('Se planifie dans Ventes & Ops / Charges directes')}>{p('Ventes & Ops / Charges directes')}</span>
          )}
          {isSalesGlobal && fcMode && (
            <span className="pl-calc-badge pl-src" title={p('Lue du plan des ventes : se planifie dans Ventes & Ops / Prévisions')}>{p('Ventes / Prévisions')}</span>
          )}
          {isPeopleLocked && fcMode && (
            nav && nav.setPage
              ? <button type="button" className="pl-calc-badge pl-src pl-src--lien" title={p('Ouvrir Ventes & Ops / Salaires (la même page que Finance › Planification › Salaires prévisionnels)')}
                        onClick={(e) => { e.stopPropagation(); nav.setPage('fin-payroll'); }}>{p('Ventes & Ops / Salaires')}</button>
              : <span className="pl-calc-badge pl-src" title={p('Se planifie dans Ventes & Ops / Salaires (la même page que Finance › Planification › Salaires prévisionnels)')}>{p('Ventes & Ops / Salaires')}</span>
          )}
          {isCapexLocked && fcMode && (
            <span className="pl-calc-badge pl-src" title={p('Amortissement du parc existant : se planifie dans Finance / Parc existant')}>{p('Finance / Parc existant')}</span>
          )}
          {isComputedLine && fcMode && row.computed === 'stock_variation' && (
            <span className="pl-calc-badge" title={p('Calculée : coût des produits vendus du plan des ventes − Achats')}>{p('calculée')}</span>
          )}
          {isComputedLine && fcMode && row.computed !== 'stock_variation' && (
            <span className="pl-calc-badge" title={p('Reprise du solde d\'acomptes clients, réglée dans Trésorerie › Acomptes clients')}>{p('Acomptes clients')}</span>
          )}
          {isManualOnly && !isSopOwned && fcMode && (
            <span className="pl-calc-badge" title={p('Cette ligne se tape : le coût des produits vendus vient du plan des ventes')}>{p('manuel')}</span>
          )}
          {hasAdjs && !hasAccts && <span className="pl-count pl-adj-badge">{(row.adjustments || []).length} adj.</span>}
          {/* Un compte sous la règle « Charges fixes » garde son choix de règle :
              on en sort en choisissant une autre (2026-09-29). */}
          {fcMode && !isDerivedVar && !isPlanAmort && !isSopOwned && !isSalesGlobal && !isPeopleLocked && !isCapexLocked && !isManualOnly && !isComputedLine && (
            <span className="pl-fc-inline" onClick={e => e.stopPropagation()}>
              {(role === 'social_charges' || role === 'income_tax' || row.section === 'tax') ? (
                <ForecastBtn
                  role={role === 'social_charges' ? 'social_charges' : 'income_tax'}
                  year={view.year}
                  rate={lineFcMap[row.code]?.forecast_rate}
                  method={lineFcMap[row.code]?.forecast_method}
                  tauxConstate={view.taux_cs_constate}
                  onFillCs={onFillCs}
                  onFillTax={onFillTax}
                  onClear={() => onClearRate && onClearRate(row.code)}
                  onConnectPeople={onSetMethod ? () => onSetMethod(row.code, { forecast_method: 'people_plan' }) : null}
                  addToast={addToast}
                />
              ) : (
                <MethodChip
                  method={lineFcMap[row.code]?.forecast_method}
                  rate={lineFcMap[row.code]?.forecast_rate}
                  baseId={lineFcMap[row.code]?.forecast_base_line_id}
                  target={lineFcMap[row.code]?.forecast_target}
                  isSalary={role === 'salary'}
                  isPersonnel={row.section === 'personnel'}
                  isRevenue={row.section === 'revenue'}
                  isPurchases={role === 'purchases'}
                  isCogs={row.section === 'cogs'}
                  isStockVar={role === 'stock_variation'}
                  isOpex={row.section === 'other_opex'}
                  choices={row.rule_choices || null}
                  lines={view.lines || []}
                  selfId={row.code}
                  onSet={patch => onSetMethod(row.code, patch)}
                />
              )}
            </span>
          )}
        </td>
        {inl(months(row.months).map((v, i) => (
          <td key={i} className={`${nCls(v, i)}${isCalc ? ' pl-num--calc' : ''}`}>
              {row.special_ops && row.special_ops[String(i + 1)] && nav && nav.setPage
                /* Un mois où une OPÉRATION SPÉCIALE joue (2026-10-22) : le montant s'écrit en orange et mène à elle. */
                ? <button type="button" className="pl-ops" title={p('Opération spéciale : ouvrir')}
                          onClick={() => nav.setPage('special-ops')}>{fmt(v)}</button>
                : isForecast(i) && canForecast && !isCalc
                  ? <InlineNum value={v} thousands={thousands} forecast onSave={nv => onEditForecast(row.code, i + 1, nv)} />
                  : fmt(v)}
            </td>
        )), ytdTd(row.total_ytd), togoTd(row.total, row.total_ytd))}
        {tailCells(row.total_ytd, row.total, row.code)}

      </tr>
    );
  }

  // Ligne d'ajustement : correction manuelle en orange, visible quand la ligne est dépliée.
  function adjRow(adj) {
    return (
      <tr key={adj.adj_line_id} className="pl-row pl-row--adjustment">
        <td className="pl-line pl-adj-cell">
          <span className="pl-adj-ref">{adj.entry_ref}</span>
          {adj.description && <span className="pl-adj-desc"> — {adj.description}</span>}
          {adj.memo && <span className="pl-adj-memo"> ({adj.memo})</span>}
        </td>
        {inl(monthLabels.map((_, i) => (
          <td key={i} className={`pl-num pl-num--adj${isForecast(i) ? ' pl-mforecast' : ''}${fcEdge(i)}`}>
              {i + 1 === adj.month ? fmt(adj.amount) : ''}
            </td>
        )), blankYtd(' pl-num--adj'), blankTogo(' pl-num--adj'))}
        {noPct ? (
          <>
            <td className="pl-spacer" />
            <td className="pl-num pl-num--adj pl-cumul-chf" />
            <td className="pl-spacer" />
            <td className="pl-num pl-num--adj pl-blk2025 pl-2025-chf" />
            <td className="pl-spacer" />
            <td className="pl-num pl-num--adj pl-ecart-chf" />
          </>
        ) : actualsOnly ? (
          <>
            <td className="pl-spacer" />
            <td className="pl-num pl-num--adj pl-cumul-chf" />
            <td className="pl-num pl-num--adj pl-cumul-pct" />
            <td className="pl-spacer" />
            <td className="pl-num pl-num--adj pl-blk2025 pl-2025-chf" />
            <td className="pl-num pl-num--adj pl-blk2025 pl-2025-pct" />
            <td className="pl-spacer" />
            <td className="pl-num pl-num--adj pl-ecart-chf" />
            <td className="pl-num pl-num--adj pl-ecart-pct" />
          </>
        ) : (
          <>
            {annCols.map((id) => (
              <td key={id} className={`pl-num pl-num--adj ${annCls(id)}`} />
            ))}
          </>
        )}
      </tr>
    );
  }

  // Ligne de compte : peinte (affectée) en brush mode.
  function accRow(a, extraCls = '') {
    // Le glisser-déposer d'un compte est la façon de RESTRUCTURER : il doit
    // exister dans le mode Éditer réel, où l'on reclasse (il y était interdit —
    // constat 2026-08-16). La poignée est la cellule du LIBELLÉ, pour ne pas
    // gêner la saisie des montants dans les cellules de mois.
    const drag = !readOnly;
    return (
      <tr
        key={a.account_number}
        className={`pl-row pl-row--acc${drag ? ' pl-draggable' : ''}${extraCls}${brushMode ? ' pl-paintable' : ''}${dragAcc === a.account_number ? ' pl-dragging' : ''}`}

        // Brush : cliquer un compte l'affecte à la ligne armée. Le brush n'existe
        // qu'en mode Édition, donc gater sur `!editMode` rendait le clic mort.
        onClick={brushMode ? () => onPaint(a.account_number) : undefined}
      >
        <td
          className="pl-line pl-acc-cell"
          draggable={drag}
          onDragStart={drag ? e => { setDragAcc(a.account_number); e.dataTransfer.effectAllowed = 'move'; } : undefined}
          onDragEnd={drag ? () => { setDragAcc(null); setOverCode(null); } : undefined}
          title={drag ? p('Glisser vers une autre ligne pour reclasser ce compte') : undefined}
        >
          <span className="pl-mono">{a.account_number}</span>
          <span className="pl-acc-desc">{a.account_desc}</span>
        </td>
        {inl(months(a.months).map((v, i) => (
          <td key={i} className={nCls(v, i)}>
              {/* Forecast au niveau ligne uniquement → comptes en lecture seule sur les mois futurs */}
              {editMode && !isForecast(i)
                ? <InlineNum value={v} thousands={thousands} onSave={nv => onEditValue(a.account_number, i + 1, nv)} />
                : (isForecast(i) ? <span className="pl-td-muted">–</span> : fmt(v))}
            </td>
        )), ytdTd(a.total_ytd), togoTd(a.total, a.total_ytd))}
        {tailCells(a.total_ytd, a.total)}
      </tr>
    );
  }

  return (
  <>
    <div className="page-table-wrap">
      {/* `pl-actuals` porte la CHARTE de la table (palette, graisses, filets) —
          elle n'était posée qu'en vue réelle ou en édition, si bien que le mode
          « Présenter » du prévisionnel changeait de police et de densité
          (constat 2026-08-16). Elle est désormais toujours appliquée : seules
          les COLONNES changent d'une vue à l'autre. */}
      <table className="page-table pl-table pl-actuals" data-tour="pl-actuals">
        <thead>
          {(actualsOnly || ytdInline) && (
            <tr className="pl-grouphead">
              {/* Impression : pas de titre de table (doublon du titre de page). */}
              <th className="pl-col-line pl-th-title">{noPct ? '' : p('Compte de résultat')}</th>
              {/* Bandeau standard RÉALISÉ / PRÉVISIONNEL (2026-08-20, ex
                  « Mois N » + « Cumulé N » + « Prévisionnel ») : le réalisé
                  couvre les mois clos ET leur cumul ; le prévisionnel, les
                  mois à venir. Même famille que le Plan produits/services. */}
              {actualsOnly ? (
                // Impression (noPct) : pas de bandeau « RÉALISÉ » — cellule vide
                // sur mois + Total réalisé (le spacer d'écran est retiré).
                noPct
                  ? <th colSpan={visCount + 1} />
                  : <th className="lk-band lk-band--real" colSpan={visCount + 3}>
                      {t('finance:reports.bandReal')}
                    </th>
              ) : (
                <>
                  {closedCount > 0 && (
                    <th className="lk-band lk-band--real" colSpan={closedCount + (ytdInline ? 1 : 0)}>{t('finance:reports.bandReal')}</th>
                  )}
                  {closedCount < 12 && (
                    <th className="lk-band lk-band--fore" colSpan={12 - closedCount + (ytdInline ? 1 : 0)}>{t('finance:reports.bandFore')}</th>
                  )}
                  <th className="pl-grp-cumul" colSpan={annCols.length}>{p('Année ')}{view.year}</th>
                  {prevView && <th className="pl-grp-2025 pl-blk2025" colSpan={3}>{cmpTitle}</th>}
                </>
              )}
              {actualsOnly && <th className="pl-spacer" />}
              {actualsOnly && (noPct ? (
                <>
                  <th className="pl-grp-2025 pl-blk2025">{compareLabel || `${p('Exercice ')}${prevYear}`}</th>
                  <th className="pl-spacer" />
                  <th className="pl-grp-ecart" colSpan={2}>{compareLabel ? p('Écart au plan') : p('Écarts')}</th>
                </>
              ) : (
                <th className="pl-grp-2025 pl-blk2025" colSpan={3}>{cmpTitle}</th>
              ))}
            </tr>
          )}
          <tr className="pl-colhead">
            {/* Le titre vit sur la rangée « Mois N » (grouphead) en vue réelle ;
                en prévisionnel (pas de grouphead), il prend la place du libellé. */}
            <th className={`pl-col-line${actualsOnly ? '' : ' pl-th-title'}`}>{actualsOnly ? '' : p('Compte de résultat')}</th>
            {inl(monthLabels.map((mo, i) => (
              <th key={i} className={`pl-col-num${fcEdge(i)}`}>{mo}</th>
            )),
            <th key="ytd-in" className="pl-col-num pl-col-total pl-col-ytd pl-ytd-inline">{t('finance:reports.cumulLabel')}</th>,
            <th key="togo-in" className="pl-col-num pl-col-togo pl-togo-inline">{p('Total à venir')}</th>)}
            {noPct ? (
              <>
                <th className="pl-col-num pl-cumul-chf">{p('Total')}</th>
                <th className="pl-spacer" />
                {/* N-1 = cumul MÊME PÉRIODE (mois 1..clôture) → sous-titre « Jul cum. » */}
                <th className="pl-col-num pl-blk2025 pl-2025-chf">
                  {closedCount > 0 ? `${monthLabels[closedCount - 1]} cum.` : 'CHF'}
                </th>
                <th className="pl-spacer" />
                <th className="pl-col-num pl-ecart-chf">abs.</th>
                <th className="pl-col-num pl-ecart-pct">%</th>
              </>
            ) : actualsOnly ? (
              <>
                <th className="pl-spacer" />
                <th className="pl-col-num pl-cumul-chf">{t('finance:reports.cumulLabel')}</th>
                <th className="pl-col-num pl-cumul-pct">% CAN</th>
                <th className="pl-spacer" />
                <th className="pl-col-num pl-blk2025 pl-2025-chf">CHF</th>
                <th className="pl-col-num pl-ecart-chf">{p('Écart')}</th>
                <th className="pl-col-num pl-ecart-pct">Delta %</th>
              </>
            ) : (
              <>
                {showFy && <th className={`pl-col-num pl-col-fy ${annCls('fy')}`}>Total</th>}
                {!ytdInline && showYtd && <th className={`pl-col-num pl-col-total pl-col-ytd ${annCls('ytd')}`}>{t('finance:reports.cumulLabel')}</th>}
                {ytdInline && !noCanPct && <th className={`pl-col-num ${annCls('pct', 'pl-annual-pct')}`}>% CAN</th>}
                {prevView && <th className="pl-col-num pl-col-fy pl-blk2025">{compareLabel ? compareLabel : 'Total N-1'}</th>}
                {prevView && <th className="pl-col-num pl-ecart-chf">{p('Écart')}</th>}
                {prevView && <th className="pl-col-num pl-ecart-pct">Delta %</th>}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {orderedRows.map(row => {
            if (row.type === 'subtotal') {
              if (!subOK(row.code)) return null;
              const grand = row.code === 'net_result';
              // Sous-total de GROUPE (2ᵉ niveau des charges directes) : même
              // rangée, présentation en retrait (2026-08-16).
              const isGroup = row.code === 'cogs_goods' || row.code === 'cogs_other';
              // Un sous-total de groupe fait partie du DÉTAIL des charges
              // directes : section repliée → il disparaît avec ses lignes,
              // sinon l'écran montre deux totaux sans les comptes qui les
              // composent (constat Olivier 2026-08-17).
              if (isGroup && !isSecOpen('cogs')) return null;
              const pctLabel = PCT_ROWS[row.code];
              const rowMonths = months(row.months);
              return (
                <React.Fragment key={row.code}>
                  <tr
                    className={`pl-row pl-row--subtotal${grand ? ' pl-row--grand' : ''}${isGroup ? ' pl-row--group pl-collapsible' : ''}`}
                    onClick={isGroup ? () => toggleGroup(row.code) : undefined}
                  >
                    <td className="pl-line">
                      {isGroup && (
                        <span className="pl-caret"><PlChevron open={!foldedGroups.has(row.code)} /></span>
                      )}
                      {row.label}
                    </td>
                    {inl(rowMonths.map((v, i) => (
                      <td key={i} className={`pl-num${isForecast(i) ? ' pl-mforecast' : ''}${fcEdge(i)}`}>{fmt(v)}</td>
                    )), ytdTd(row.total_ytd), togoTd(row.total, row.total_ytd))}
                    {tailCells(row.total_ytd, row.total, row.code)}
                  </tr>
                  {/* Vue complète : sous-ligne % pour les marges de PCT_ROWS. */}
                  {pctLabel && !actualsOnly && (
                    <tr className="pl-row pl-row--pct">
                      <td className="pl-line pl-pct-label">{pctLabel}</td>
                      {inl(rowMonths.map((v, i) => (
                        <td key={i} className={`pl-num pl-num--pct${isForecast(i) ? ' pl-mforecast' : ''}${fcEdge(i)}`}>
                            {pct(v, revMonths[i])}
                          </td>
                      )), blankYtd(' pl-num--pct'), blankTogo(' pl-num--pct'))}
                      {showFy && <td className={`pl-num pl-num--pct pl-num--fy ${annCls('fy')}`}>{pct(row.total, revFy)}</td>}
                      {!ytdInline && showYtd && <td className={`pl-num pl-num--pct pl-num--ytd ${annCls('ytd')}`}>{pct(row.total_ytd, revYtd)}</td>}
                      {ytdInline && !noCanPct && <td className={`pl-num pl-num--pct ${annCls('pct', 'pl-annual-pct')}`} />}
                      {prevView && <td className="pl-num pl-num--pct pl-blk2025" />}
                      {prevView && <td className="pl-num pl-num--pct pl-ecart-chf" />}
                      {prevView && <td className="pl-num pl-num--pct pl-ecart-pct" />}
                    </tr>
                  )}
                  {/* Réel seul : % mensuel du CA net sous Valeur ajoutée et Marge brute
                      (le cumul % CAN reste porté par la colonne de droite). */}
                  {/* Impression : seule la Marge brute garde sa sous-ligne %. */}
                  {actualsOnly && PCT_OF_CAN.includes(row.code) && !(noPct && row.code === 'value_added') && (
                    <tr className="pl-row pl-row--pct">
                      <td className="pl-line pl-pct-label">en % du CA net</td>
                      {rowMonths.map((v, i) => (
                        <td key={i} className={`pl-num pl-num--pct${isForecast(i) ? ' pl-mforecast' : ''}${fcEdge(i)}`}>
                            {pct(v, revMonths[i])}
                          </td>
                      ))}
                      {/* Queue alignée sur tailCells — cellules laissées vides (pas de
                          colonnes %). noPct : 3 colonnes CHF ; sinon : bloc complet. */}
                      {noPct ? (
                        <>
                          {/* % aussi sur Total et N-1 (pas sur les écarts) —
                              même gabarit de cellules que tailCells (noPct). */}
                          <td className="pl-num pl-cumul-chf pl-num--pct">{pctCan(row.total_ytd, revYtd)}</td>
                          <td className="pl-spacer" />
                          <td className="pl-num pl-blk2025 pl-2025-chf pl-num--pct">{pctCan(priorByCode[row.code], prevRevYtd)}</td>
                          <td className="pl-spacer" />
                          <td className="pl-num pl-ecart-chf pl-num--pct" />
                          <td className="pl-num pl-ecart-pct pl-num--pct" />
                        </>
                      ) : (
                        <>
                          <td className="pl-spacer" />
                          <td className="pl-num pl-cumul-chf pl-num--pct" />
                          <td className="pl-num pl-cumul-pct pl-num--pct" />
                          <td className="pl-spacer" />
                          <td className="pl-num pl-2025-chf pl-num--pct" />
                          <td className="pl-num pl-ecart-chf pl-num--pct" />
                          <td className="pl-num pl-ecart-pct pl-num--pct" />
                        </>
                      )}
                    </tr>
                  )}
                </React.Fragment>
              );
            }
            if (row.type === 'section_total') {
              if (!secOK(row.section)) return null;
              const has = !noFold && secHasLines.has(row.section);
              const open = isSecOpen(row.section);
              return (
                <tr
                  key={row.code}
                  className={`pl-row pl-row--sectiontotal${row.section === 'revenue' ? ' pl-row--headline' : ''}${has ? ' pl-collapsible' : ''}`}
                  onClick={has ? () => onToggleSection(row.section) : undefined}
                >
                  <td className="pl-line">
                    <span className="pl-caret">{has ? <PlChevron open={open} /> : null}</span>
                    {row.label}
                  </td>
                  {inl(months(row.months).map((v, i) => (
                    <td key={i} className={nCls(v, i)}>{fmt(v)}</td>
                  )), ytdTd(row.total_ytd), togoTd(row.total, row.total_ytd))}
                  {tailCells(row.total_ytd, row.total, row.code)}
                </tr>
              );
            }
            // detail : visible seulement si sa catégorie est dans le filtre et dépliée.
            if (!secOK(row.section)) return null;
            if (!isSecOpen(row.section)) return null;
            // Groupe replié (charges directes) : ses lignes disparaissent, son
            // total reste en tête (2026-08-16).
            if (row.group && foldedGroups.has(row.group)) return null;
            // Sous-traitance hors stock : sautée ici, rendue sous le Coût des ventes.
            if (row.line_role === 'subcontracting' && subOutOfGroup) return null;
            return (
              <React.Fragment key={row.code}>
                {lineRow(row)}
                {isOpen(row.code) && row.accounts.map(a => accRow(a))}
                {isOpen(row.code) && (row.adjustments || []).map(adj => adjRow(adj))}
                {/* Sous-total « Coût des ventes » supprimé le 2026-08-16 : la
                    consommation se lit dans les lignes elles-mêmes, ce mémo
                    n'apportait qu'un total non sommé au compte de résultat. */}
                {canForecast && row.line_role === 'stock_variation' && (
                  <>
                    {subOutOfGroup && lineRow(subRow)}
                    {subOutOfGroup && isOpen(subRow.code) && subRow.accounts.map(a => accRow(a))}
                    {subOutOfGroup && isOpen(subRow.code) && (subRow.adjustments || []).map(adj => adjRow(adj))}
                  </>
                )}
              </React.Fragment>
            );
          })}

          {!sectionFilter && view.unmapped.length > 0 && (
            <>
              <tr
                className={`pl-row pl-row--line pl-row--unmapped${overCode === 'unmapped' ? ' pl-droptarget' : ''}`}
                {...dropProps('unmapped', null)}
              >
                <td className="pl-line">
                  Non mappé — {brushMode ? 'clique un compte' : p('glisse un compte ici')}
                </td>
                {inl(monthLabels.map((_, i) => (
                  <td key={i} className={`pl-num${isForecast(i) ? ' pl-mforecast' : ''}${fcEdge(i)}`}>
                      {fmt(view.unmapped.reduce((s, a) => s + Number(a.months[String(i + 1)] || 0), 0))}
                    </td>
                )), ytdTd(view.unmapped.reduce((s, a) => s + (a.total_ytd || 0), 0)),
                togoTd(view.unmapped.reduce((s, a) => s + a.total, 0), view.unmapped.reduce((s, a) => s + (a.total_ytd || 0), 0)))}
                {tailCells(
                  view.unmapped.reduce((s, a) => s + (a.total_ytd || 0), 0),
                  view.unmapped.reduce((s, a) => s + a.total, 0),
                )}
              </tr>
              {view.unmapped.map(a => accRow(a, ' pl-row--unmapped'))}
            </>
          )}
        </tbody>
      </table>
    </div>
    {/* LE TAUX CS SUIT SA SECTION (2026-09-19) : il fermait la table sur toutes
        les étapes, revenus et autres charges compris, où il ne se rapporte à
        rien de ce qu'on lit. Il ne paraît donc que là où le PERSONNEL est à
        l'écran, seul endroit où il explique un chiffre. */}
    {view.taux_cs_constate != null && secOK('personnel') && (
      <div className="pl-kpi-taux">
        Taux CS constaté (réalisé) — {(view.taux_cs_constate * 100).toFixed(1)} %
      </div>
    )}
  </>
  );
}
